import { filledInputClasses } from '@mui/material/FilledInput';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputLabelClasses } from '@mui/material/InputLabel';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { alpha, Theme } from '@mui/material/styles';

import { deepNavy } from '../../palette-v2';

// ----------------------------------------------------------------------
// V2 Design System — TextField overrides using Figma "specific - components" Input tokens
// ----------------------------------------------------------------------

export function textFieldV2(theme: Theme) {
  const color = {
    focused: deepNavy[900], // Input/Text/focus → deepNavy/700, but border/focus → deepNavy/900
    active: deepNavy[600], // Input/Text/label
    placeholder: deepNavy[500], // Input/Text/placeholder
  };

  const border = {
    enable: deepNavy[500], // Input/Border/enable
    hover: deepNavy[700], // Input/Border/hover
    focus: deepNavy[900], // Input/Border/focus
    disable: deepNavy[300], // Input/Border/disable
  };

  const fill = {
    enable: '#ffffff', // Input/Fill/enable
    hover: deepNavy[50], // Input/Fill/hover
    focus: deepNavy[200], // Input/Fill/focus
    disable: deepNavy[300], // Input/Fill/disable
  };

  const font = {
    label: theme.typography.body1,
    value: theme.typography.body2,
  };

  return {
    // HELPER
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: theme.spacing(1),
        },
      },
    },

    // LABEL
    MuiFormLabel: {
      styleOverrides: {
        root: {
          ...font.value,
          color: color.placeholder,
          [`&.${inputLabelClasses.shrink}`]: {
            ...font.label,
            fontWeight: 600,
            color: color.active,
            [`&.${inputLabelClasses.focused}`]: {
              color: color.focused,
            },
            [`&.${inputLabelClasses.error}`]: {
              color: theme.palette.error.main,
            },
            [`&.${inputLabelClasses.disabled}`]: {
              color: deepNavy[300],
            },
            [`&.${inputLabelClasses.filled}`]: {
              transform: 'translate(12px, 6px) scale(0.75)',
            },
          },
        },
      },
    },

    // BASE
    MuiInputBase: {
      styleOverrides: {
        root: {
          [`&.${inputBaseClasses.disabled}`]: {
            '& svg': {
              color: deepNavy[300],
            },
          },
        },
        input: {
          ...font.value,
          '&::placeholder': {
            opacity: 1,
            color: color.placeholder,
          },
        },
      },
    },

    // STANDARD
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:before': {
            borderBottomColor: border.enable,
          },
          '&:after': {
            borderBottomColor: border.focus,
          },
        },
      },
    },

    // OUTLINED
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          [`&.${outlinedInputClasses.focused}`]: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: border.focus,
            },
          },
          [`&:hover`]: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: border.hover,
            },
          },
          [`&.${outlinedInputClasses.error}`]: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: theme.palette.error.main,
            },
          },
          [`&.${outlinedInputClasses.disabled}`]: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: border.disable,
            },
          },
        },
        notchedOutline: {
          borderColor: border.enable,
          transition: theme.transitions.create(['border-color'], {
            duration: theme.transitions.duration.shortest,
          }),
        },
      },
    },

    // FILLED
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: theme.shape.borderRadius,
          backgroundColor: fill.enable,
          '&:hover': {
            backgroundColor: fill.hover,
          },
          [`&.${filledInputClasses.focused}`]: {
            backgroundColor: fill.focus,
          },
          [`&.${filledInputClasses.error}`]: {
            backgroundColor: alpha(theme.palette.error.main, 0.08),
            [`&.${filledInputClasses.focused}`]: {
              backgroundColor: alpha(theme.palette.error.main, 0.16),
            },
          },
          [`&.${filledInputClasses.disabled}`]: {
            backgroundColor: fill.disable,
          },
        },
      },
    },
  };
}
